import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { generateArtifacts } from '../services/artifactGeneratorService';
import '../styles/generation.css';

function Generation() {
  const navigate = useNavigate();
  const { sessionData } = useSession();
  const [artifacts, setArtifacts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeContentTab, setActiveContentTab] = useState('preview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionData?.projectBrief) {
      navigate('/architecture-design');
      return;
    }

    // Get selected artifacts from session - should have been set by Artifacts page
    const selectedArtifacts = sessionData.selectedArtifacts || [];
    
    if (selectedArtifacts.length === 0) {
      console.warn('No selected artifacts found, redirecting to artifacts page');
      navigate('/artifacts');
      return;
    }
    const presetTier = sessionData.currentPreset || 'Starter';

    console.log('🔍 Generation Debug:', {
      selectedArtifacts,
      presetTier,
      sessionData: sessionData
    });

    try {
      // Generate artifacts for implemented ones, create placeholders for others
      const implementedArtifacts = ['PRD', 'C4-CTX'];
      const allArtifacts = selectedArtifacts.map(artifactId => {
        if (implementedArtifacts.includes(artifactId)) {
          // Generate the actual artifact
          const generated = generateArtifacts([artifactId], sessionData.projectBrief, presetTier);
          return generated[0];
        } else {
          // Create placeholder
          return {
            id: artifactId,
            name: getArtifactDisplayName(artifactId),
            format: getArtifactFormat(artifactId),
            filename: `${artifactId.toLowerCase()}.txt`,
            content: `# ${getArtifactDisplayName(artifactId)}\n\nThis artifact is coming soon! We're working on implementing generation for this type of documentation.\n\nIn the meantime, you can use this space to manually create your ${getArtifactDisplayName(artifactId).toLowerCase()}.`,
            isPlaceholder: true
          };
        }
      });
      
      console.log('✅ Generated artifacts:', allArtifacts);
      setArtifacts(allArtifacts);
    } catch (error) {
      console.error('Error generating artifacts:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionData, navigate]);

  const getArtifactDisplayName = (artifactId) => {
    const names = {
      'PRD': 'Product Requirements Document',
      'C4-CTX': 'C4 Context Diagram',
      'C4-CNT': 'C4 Container Diagram',
      'HLD': 'High-Level Design',
      'ERD': 'Entity Relationship Diagram',
      'SEQ': 'Sequence Diagram',
      'API': 'API Specification',
      'DEPLOY': 'Deployment Architecture',
      'SEC': 'Security Architecture',
      'DATA': 'Data Architecture'
    };
    return names[artifactId] || artifactId;
  };

  const getArtifactFormat = (artifactId) => {
    const formats = {
      'PRD': 'markdown',
      'C4-CTX': 'plantuml',
      'C4-CNT': 'plantuml',
      'HLD': 'markdown',
      'ERD': 'plantuml',
      'SEQ': 'plantuml',
      'API': 'yaml',
      'DEPLOY': 'plantuml',
      'SEC': 'markdown',
      'DATA': 'markdown'
    };
    return formats[artifactId] || 'markdown';
  };

  const handleDownload = (artifact) => {
    const blob = new Blob([artifact.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEdit = (index, newContent) => {
    setArtifacts(prev => 
      prev.map((artifact, i) => 
        i === index ? { ...artifact, content: newContent } : artifact
      )
    );
  };

  const renderMarkdown = (content) => {
    return content
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/\n\n/gim, '</p><p>')
      .replace(/^(?!<[h|l|b])/gim, '<p>')
      .replace(/$/gim, '</p>');
  };

  if (loading) {
    return (
      <div className="generation-page">
        <div className="loading-state">
          <h2>Generating Artifacts...</h2>
          <p>Creating your documentation based on the project brief.</p>
        </div>
      </div>
    );
  }

  if (artifacts.length === 0) {
    return (
      <div className="generation-page">
        <div className="error-state">
          <h2>No Artifacts Selected</h2>
          <p>Please go back and select artifacts to generate.</p>
          <button onClick={() => navigate('/artifacts')} className="btn-primary">
            Back to Artifacts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="generation-page">
      <div className="generation-header">
        <h1>Generated Artifacts</h1>
        <p>Your documentation has been generated based on the project brief. You can preview, edit, and download each artifact.</p>
      </div>

      <div className="artifacts-tabs">
        <div className="tab-headers">
          {artifacts.map((artifact, index) => (
            <button
              key={artifact.id}
              className={`tab-header ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {artifact.name}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {artifacts.map((artifact, index) => (
            <div
              key={artifact.id}
              className={`tab-panel ${activeTab === index ? 'active' : ''}`}
            >
              <div className="artifact-controls">
                <div className="artifact-info">
                  <h3>{artifact.name}</h3>
                  <div className="badges-group">
                    <span className="format-badge">{artifact.format}</span>
                    {artifact.isPlaceholder && (
                      <span className="placeholder-badge">Coming Soon</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(artifact)}
                  className="btn-download"
                  disabled={artifact.isPlaceholder}
                >
                  {artifact.isPlaceholder ? 'Not Available' : `Download ${artifact.filename}`}
                </button>
              </div>

              <div className="artifact-content">
                <div className="content-tabs">
                  <button 
                    className={`content-tab ${activeContentTab === 'preview' ? 'active' : ''}`}
                    onClick={() => setActiveContentTab('preview')}
                  >
                    Preview
                  </button>
                  <button 
                    className={`content-tab ${activeContentTab === 'edit' ? 'active' : ''}`}
                    onClick={() => setActiveContentTab('edit')}
                  >
                    Edit
                  </button>
                </div>

                <div className="content-area">
                  {activeContentTab === 'preview' ? (
                    artifact.format === 'markdown' ? (
                      <div className="markdown-preview">
                        <div className="rendered-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(artifact.content) }} />
                      </div>
                    ) : (
                      <div className="plantuml-preview">
                        <pre>{artifact.content}</pre>
                        <div className="plantuml-note">
                          <p><strong>Note:</strong> This is PlantUML code. To render the diagram:</p>
                          <ol>
                            <li>Copy the code above</li>
                            <li>Paste it into <a href="http://www.plantuml.com/plantuml/uml" target="_blank" rel="noopener noreferrer">PlantUML Online</a></li>
                            <li>Or use a PlantUML plugin in your IDE</li>
                          </ol>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="edit-area">
                      <textarea
                        value={artifact.content}
                        onChange={(e) => handleEdit(index, e.target.value)}
                        className="content-editor"
                        placeholder="Edit your content here..."
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="generation-actions">
        <button 
          className="btn-secondary"
          onClick={() => navigate('/artifacts')}
        >
          ← Back to Artifacts
        </button>
        
        <button 
          className="btn-primary"
          onClick={() => {
            artifacts.forEach(artifact => handleDownload(artifact));
          }}
        >
          Download All
        </button>
      </div>
    </div>
  );
}

export default Generation;